import { useCallback, useMemo, useRef, useState } from "react";

/**
 * The calendar's single drawer slot (design handoff "Drawer rules"): only one
 * drawer surface is open at a time. Opening a kind evicts whatever kind holds
 * the slot (its registered evictor closes it), so callers never juggle "close
 * the others" themselves.
 *
 * Kinds today: "appointment" (the event preview popover until the
 * appointment drawer replaces it), "booking" (the booking drawer / mobile
 * sheet), "timeBlock" (the time block drawer) and "walkins" (the walk-in
 * waitlist drawer; nested "walkInForm" = New / Edit walk-in).
 */
export type DrawerKind = "appointment" | "booking" | "timeBlock" | "walkins";

export interface DrawerSlot {
  kind: DrawerKind;
  /** What the drawer shows (transaction id, booking mode, ...). */
  id?: string;
  /** The open nested screen inside the drawer, if any. */
  nested: string | null;
}

export type DrawerSlotAction =
  | { type: "open"; kind: DrawerKind; id?: string }
  | { type: "close"; kind: DrawerKind }
  | { type: "openNested"; nested: string }
  | { type: "closeNested" };

/** Pure transition. Returns the same object when nothing changes. */
export function drawerSlotReducer(
  state: DrawerSlot | null,
  action: DrawerSlotAction,
): DrawerSlot | null {
  switch (action.type) {
    case "open":
      if (state && state.kind === action.kind && state.id === action.id) {
        return state;
      }
      // A different drawer (or a different subject) starts with no nested
      // screen open.
      return { kind: action.kind, id: action.id, nested: null };
    case "close":
      return state && state.kind === action.kind ? null : state;
    case "openNested":
      if (!state || state.nested === action.nested) return state;
      return { ...state, nested: action.nested };
    case "closeNested":
      return state && state.nested ? { ...state, nested: null } : state;
    default:
      return state;
  }
}

export interface DrawerSlotApi {
  slot: DrawerSlot | null;
  /** Take the slot for `kind`, evicting the kind that holds it. */
  open: (kind: DrawerKind, id?: string) => void;
  /** Release the slot if `kind` holds it (no-op otherwise). */
  close: (kind: DrawerKind) => void;
  openNested: (nested: string) => void;
  closeNested: () => void;
  /**
   * How `kind` closes when another kind takes the slot. Consumers register
   * once (a stable callback reading refs); the latest registration wins.
   */
  setEvictor: (kind: DrawerKind, evict: () => void) => void;
}

/**
 * Ref-shadowed store (same pattern as useBookingSession's `run`): actions
 * apply to the latest state synchronously, so an evictor that itself calls
 * `close` composes with the `open` that triggered it. Evictors run in the
 * caller's event handler, never inside a state updater.
 */
export function useDrawerSlot(): DrawerSlotApi {
  const [slot, setSlot] = useState<DrawerSlot | null>(null);
  const slotRef = useRef<DrawerSlot | null>(slot);
  const evictorsRef = useRef<Partial<Record<DrawerKind, () => void>>>({});

  const run = useCallback((action: DrawerSlotAction) => {
    const next = drawerSlotReducer(slotRef.current, action);
    if (next !== slotRef.current) {
      slotRef.current = next;
      setSlot(next);
    }
  }, []);

  const open = useCallback(
    (kind: DrawerKind, id?: string) => {
      const holder = slotRef.current;
      if (holder && holder.kind !== kind) {
        // The evictor normally calls close(holder.kind) itself; release the
        // slot regardless so a missing evictor can't leave two kinds open.
        evictorsRef.current[holder.kind]?.();
        run({ type: "close", kind: holder.kind });
      }
      run({ type: "open", kind, id });
    },
    [run],
  );

  const close = useCallback(
    (kind: DrawerKind) => run({ type: "close", kind }),
    [run],
  );
  const openNested = useCallback(
    (nested: string) => run({ type: "openNested", nested }),
    [run],
  );
  const closeNested = useCallback(() => run({ type: "closeNested" }), [run]);
  const setEvictor = useCallback((kind: DrawerKind, evict: () => void) => {
    evictorsRef.current[kind] = evict;
  }, []);

  return useMemo(
    () => ({ slot, open, close, openNested, closeNested, setEvictor }),
    [slot, open, close, openNested, closeNested, setEvictor],
  );
}
